import {computed, inject, Injectable, resource, Signal} from '@angular/core';
import {take} from 'rxjs/operators';
import {CellPreparedEvent} from 'devextreme/ui/data_grid';
import {HttpService} from '../technique/http.service';
import {ResponseWrapper} from '../../suppliers/wrappers/response-wrapper';
import {PlcProduitComplementaireDTO} from '../../dtos/plc-produit-complementaire-dto';
import {
  ProduitDeclinaisonComplementaireDTO
} from '../../dtos/produit/declinaison/complementaire/produit-declinaison-complementaire-dto';
import {
  McpConditionnementVarianteDTO
} from '../../dtos/produit/declinaison/complementaire/mcp-conditionnement-variante-dto';
import {
  PeriodeConditionnementDTO
} from '../../dtos/produit/declinaison/complementaire/periode-conditionnement-dto';
import {UniteDeMesureDTO} from '../../dtos/unitedemesure-dto';
import {JourSemaine} from '../../enums/client/jour-semaine.enum';
import {
  JOUR_SEMAINE_LIST,
  JourSemaineItem
} from '../../models/gestion-produits/declinaison/complementaire/plc/jour-semaine-item';
import {JourColonne} from '../../models/gestion-produits/declinaison/complementaire/plc/jour-colonne';
import {
  ProduitComplementaire
} from '../../models/gestion-produits/declinaison/complementaire/produit-complementaire';
import {ProduitDeclinaisonService} from './produit-declinaison.service';
import {IconThemeEnum} from '../../../shared/ui/icon/icon-theme-enum';

export const URL_GET_UNITES_DE_MESURE = `dolrest/unite-de-mesure/get-all`;

@Injectable({
  providedIn: 'root'
})
export class ProduitComplementaireParametrageService {

  private readonly ICON_PLAT = 'utensils';
  private readonly ICON_DENREE = 'apple-alt';

  readonly COLONNE_PERIODE_DATE = 'periodeDate';
  readonly COLONNE_PERIODE_UNITE = 'periodeUnite';
  readonly COLONNE_PERIODE_RATIO = 'periodeRatio';
  readonly COLONNE_PERIODE_UNITE_TECHNIQUE = 'periodeUniteTechnique';
  readonly COLONNE_PERIODE_LIST: string[] = [this.COLONNE_PERIODE_DATE, this.COLONNE_PERIODE_UNITE,
                                             this.COLONNE_PERIODE_RATIO, this.COLONNE_PERIODE_UNITE_TECHNIQUE];

  private readonly httpSvc = inject(HttpService);

  private readonly uniteDeMesureResource = resource<UniteDeMesureDTO[], void>({
    loader: async () => {
      const response: ResponseWrapper<UniteDeMesureDTO> = await this.httpSvc.get(URL_GET_UNITES_DE_MESURE).pipe(take(1)).toPromise();
      return response?.resultList ?? [];
    }
  });

  private readonly uniteDeMesureList: Signal<UniteDeMesureDTO[]> = computed(() => this.uniteDeMesureResource.value() ?? []);

  private buildJourColumnName(jourId: JourSemaine): string {
    return `jourConsommation${jourId}`;
  }

  private buildJourColonneList(buildCaption: (jour: JourSemaineItem) => string): JourColonne[] {
    return JOUR_SEMAINE_LIST.map(jour => ({
      name: this.buildJourColumnName(jour.id),
      caption: buildCaption(jour),
      calculateCellValue: (produitComplementaire: PlcProduitComplementaireDTO): boolean =>
        (produitComplementaire?.jourConsommationList ?? []).includes(jour.id)
    }));
  }

  readonly jourColonneList: JourColonne[] = this.buildJourColonneList(jour => jour.libelleCourt);

  private readonly jourByColumnName = new Map<string, JourSemaineItem>(
    JOUR_SEMAINE_LIST.map(jour => [this.buildJourColumnName(jour.id), jour]));

  getJourLibelle = (columnName: string): string => this.jourByColumnName.get(columnName)?.libelle ?? '';

  findJourCellule = (cell: CellPreparedEvent): JourSemaineItem | undefined =>
    cell.rowType === 'data' ? this.jourByColumnName.get(cell.column?.name) : undefined;

  toggleJourConsommation = (produitComplementaireList: PlcProduitComplementaireDTO[], produitDeclinaisonId: number,
                            jourId: JourSemaine): PlcProduitComplementaireDTO[] =>
    produitComplementaireList.map(produitComplementaire => produitComplementaire.produitDeclinaisonId !== produitDeclinaisonId
      ? produitComplementaire
      : this.toggleJourConsommationLigne(produitComplementaire, jourId));

  /**
   * Bascule un jour sur une seule ligne. À préférer dès que la grille affiche plusieurs points de livraison : le
   * produit décliné y est porté par autant de lignes qu'il a de points de livraison associés.
   */
  toggleJourConsommationLigne = (produitComplementaire: PlcProduitComplementaireDTO,
                                 jourId: JourSemaine): PlcProduitComplementaireDTO => ({
    ...produitComplementaire,
    jourConsommationList: this.buildJourConsommationList(produitComplementaire.jourConsommationList ?? [], jourId)
  });

  private buildJourConsommationList = (jourConsommationList: JourSemaine[], jourId: JourSemaine): JourSemaine[] =>
    jourConsommationList.includes(jourId)
      ? jourConsommationList.filter(jour => jour !== jourId)
      : JOUR_SEMAINE_LIST
        .filter(jour => jour.id === jourId || jourConsommationList.includes(jour.id))
        .map(jour => jour.id);

  buildLigne = (declinaison: ProduitDeclinaisonComplementaireDTO): PlcProduitComplementaireDTO => ({
    produitDeclinaisonId: declinaison.id,
    facturable: false,
    commandablePortail: false,
    jourConsommationList: [],
    periodeConditionnementList: [],
    libelle: declinaison.libelle,
    declinaison: declinaison.declinaison,
    fabrique: declinaison.fabrique,
    uniteDeMesure: declinaison.uniteDeMesure,
    mcpConditionnementVarianteList: declinaison.mcpConditionnementVarianteList ?? []
  });

  /**
   * Champs d'une ligne de paramétrage en GraphQL. La même forme sert aux trois lectures : la fiche du point de
   * livraison, la fiche produit et les points de livraison encore associables.
   */
  readonly PLC_PRODUIT_COMPLEMENTAIRE_PARAMETRAGE = `
    plcId,
    produitDeclinaisonId,
    facturable,
    commandablePortail,
    libelle,
    declinaison {
      id,
      libelle,
      code
    },
    fabrique,
    uniteDeMesure {
      id,
      libelle,
      abreviation
    },
    plcCode,
    plcLibelle,
    clientLibelle,
    jourConsommationList,
    periodeConditionnementList {
      dateDebut,
      uniteDeMesureId,
      ratio,
      mcpConditionnementVarianteId
    },
    mcpConditionnementVarianteList {
      id,
      libelle
    }
  `;

  /** `jourConsommationList` arrive en noms d'enum GraphQL, l'enum TypeScript est numérique. */
  private mapJourConsommationList = (nomList: string[] | null | undefined): JourSemaine[] =>
    (nomList ?? []).map(nom => JourSemaine[nom as keyof typeof JourSemaine])
                   .filter((jour): jour is JourSemaine => jour != null);

  /** Les listes absentes de la réponse doivent rester des tableaux : la grille et les comparaisons y comptent. */
  mapLigne = (raw: any): PlcProduitComplementaireDTO => ({
    ...raw,
    jourConsommationList: this.mapJourConsommationList(raw.jourConsommationList),
    periodeConditionnementList: raw.periodeConditionnementList ?? [],
    mcpConditionnementVarianteList: raw.mcpConditionnementVarianteList ?? []
  });

  private isPlat = (produitComplementaire: ProduitComplementaire): boolean => !!produitComplementaire?.fabrique;

  getProduitLibelle = (produit: ProduitComplementaire): string => ProduitDeclinaisonService.buildLibelle(produit);

  getPlcLibelle = (produitComplementaire: PlcProduitComplementaireDTO): string =>
    produitComplementaire?.plcLibelle ?? '';

  /**
   * Désigne une ligne dans un message d'erreur : le produit décliné seul suffit vu du point de livraison, il faut
   * y adjoindre le point de livraison quand la grille en affiche plusieurs.
   */
  getLigneLibelle = (produitComplementaire: PlcProduitComplementaireDTO): string =>
    [this.getProduitLibelle(produitComplementaire), produitComplementaire?.plcLibelle]
      .filter(libelle => !!libelle)
      .join(' / ');

  /** Code et client du point de livraison : trop verbeux pour la cellule, utile en infobulle. */
  getPlcTooltip = (produitComplementaire: PlcProduitComplementaireDTO): string =>
    [produitComplementaire?.plcCode, produitComplementaire?.clientLibelle].filter(libelle => !!libelle).join(' - ');

  getNatureIcon = (produitComplementaire: ProduitComplementaire): string =>
    this.isPlat(produitComplementaire) ? this.ICON_PLAT : this.ICON_DENREE;

  getNatureTheme = (produitComplementaire: ProduitComplementaire): IconThemeEnum =>
    this.isPlat(produitComplementaire) ? IconThemeEnum.DEFAULT_INNER : IconThemeEnum.SECONDARY_INNER;

  getNatureLibelle = (produitComplementaire: ProduitComplementaire): string =>
    this.isPlat(produitComplementaire) ? 'Plat' : 'Denrée';

  /** Unités de commande proposables : les condi variants pour un plat, les unités de mesure pour une denrée. */
  uniteDeCommandeLookupSource = (produitComplementaire: PlcProduitComplementaireDTO): (McpConditionnementVarianteDTO | UniteDeMesureDTO)[] =>
    this.isPlat(produitComplementaire)
      ? produitComplementaire?.mcpConditionnementVarianteList ?? []
      : this.uniteDeMesureList();

  getUniteDeCommandeItemLibelle = (item: McpConditionnementVarianteDTO | UniteDeMesureDTO): string => item?.libelle ?? '';

  getPeriodeList = (produitComplementaire: PlcProduitComplementaireDTO): PeriodeConditionnementDTO[] =>
    produitComplementaire?.periodeConditionnementList ?? [];

  private getPeriodeUniteId = (produitComplementaire: PlcProduitComplementaireDTO, periode: PeriodeConditionnementDTO): number =>
    this.isPlat(produitComplementaire) ? periode?.mcpConditionnementVarianteId : periode?.uniteDeMesureId;

  getPeriodeUniteLibelle = (produitComplementaire: PlcProduitComplementaireDTO, periode: PeriodeConditionnementDTO): string =>
    this.getUniteDeCommandeItemLibelle(
      this.uniteDeCommandeLookupSource(produitComplementaire)
          .find(item => item.id === this.getPeriodeUniteId(produitComplementaire, periode)));

  getPeriodeRatio = (periode: PeriodeConditionnementDTO): number => periode?.ratio;

  /** Unité technique du produit décliné, quelle que soit sa nature : son unité de base. */
  getUniteTechniqueAbreviation = (produitComplementaire: PlcProduitComplementaireDTO): string =>
    produitComplementaire?.uniteDeMesure?.abreviation ?? '';

  buildPeriode = (produitComplementaire: PlcProduitComplementaireDTO, dateDebut: string, uniteId: number,
                  ratio: number): PeriodeConditionnementDTO => this.isPlat(produitComplementaire)
    ? {dateDebut, mcpConditionnementVarianteId: uniteId, ratio}
    : {dateDebut, uniteDeMesureId: uniteId, ratio};

  /** Dates ISO : comparaison et tri se font directement sur la chaîne. */
  upsertPeriodeList = (periodeList: PeriodeConditionnementDTO[],
                       periode: PeriodeConditionnementDTO): PeriodeConditionnementDTO[] =>
    [...periodeList.filter(item => item?.dateDebut !== periode.dateDebut), periode]
      .sort((first, second) => first.dateDebut.localeCompare(second.dateDebut));

  removePeriodeList = (periodeList: PeriodeConditionnementDTO[], dateDebut: string): PeriodeConditionnementDTO[] =>
    periodeList.filter(periode => periode?.dateDebut !== dateDebut);

  private isPeriodeListVide = (produitComplementaire: PlcProduitComplementaireDTO): boolean =>
    !this.getPeriodeList(produitComplementaire).length;

  private isUniteDeCommandeManquante = (produitComplementaire: PlcProduitComplementaireDTO): boolean =>
    this.getPeriodeList(produitComplementaire)
        .some(periode => !this.getPeriodeUniteId(produitComplementaire, periode));

  private isRatioManquant = (produitComplementaire: PlcProduitComplementaireDTO): boolean =>
    this.getPeriodeList(produitComplementaire).some(periode => !((periode?.ratio ?? 0) > 0));

  private isColonneObligatoireVide = (columnName: string, produitComplementaire: PlcProduitComplementaireDTO): boolean =>
    (this.COLONNE_PERIODE_LIST.includes(columnName) && this.isPeriodeListVide(produitComplementaire))
    || (columnName === this.COLONNE_PERIODE_UNITE && this.isUniteDeCommandeManquante(produitComplementaire))
    || (columnName === this.COLONNE_PERIODE_RATIO && this.isRatioManquant(produitComplementaire));

  applyCelluleClassList = (event: CellPreparedEvent, jourEditable: boolean): void => {
    if (event.rowType !== 'data') {
      return;
    }
    const columnName = event.column?.name;
    if (this.jourByColumnName.has(columnName)) {
      event.cellElement.classList.add('produit-complementaire-jour-cell');
      event.cellElement.classList.toggle('produit-complementaire-jour-cell-active', !!event.value);
      event.cellElement.classList.toggle('produit-complementaire-jour-cell-editable', jourEditable);
      return;
    }
    event.cellElement.classList.toggle('produit-complementaire-cell-mandatory',
                                       this.isColonneObligatoireVide(columnName, event.data));
  };

  buildParametrageIncompletMessage = (produitComplementaireList: PlcProduitComplementaireDTO[]): string | null => {
    const messageList = [
      this.buildRegleMessage(produitComplementaireList, this.isPeriodeListVide,
                             `Au moins une période de conditionnement est obligatoire`),
      this.buildRegleMessage(produitComplementaireList, this.isUniteDeCommandeManquante,
                             `Unité de commande obligatoire`),
      this.buildRegleMessage(produitComplementaireList, this.isRatioManquant, `Ratio supérieur à 0 obligatoire`)
    ].filter(message => !!message);

    return messageList.length ? messageList.join(' — ') : null;
  };

  private buildRegleMessage = (produitComplementaireList: PlcProduitComplementaireDTO[],
                               isInvalid: (produitComplementaire: PlcProduitComplementaireDTO) => boolean,
                               libelle: string): string | null => {
    const produitLibelleList = produitComplementaireList.filter(produitComplementaire => isInvalid(produitComplementaire))
                                                       .map(produitComplementaire => this.getLigneLibelle(produitComplementaire));

    return produitLibelleList.length ? `${libelle} : ${produitLibelleList.join(', ')}` : null;
  };
}
